/**
 * Centralized configuration constants for LexiContest client application
 *
 * Provides type-safe constants for animations, WebSocket configuration,
 * UI settings, and game logic parameters with environment support.
 */

/**
 * Animation timing constants
 *
 * CRITICAL: JavaScript timeout values must match corresponding CSS animation durations
 * to ensure proper synchronization between JS state changes and CSS animations.
 */
export const ANIMATION_CONFIG = {
  /** Letter reveal animation duration (matches CSS animation in LetterGrid) */
  LETTER_REVEAL_DURATION: 400,
  /** Button loading feedback duration */
  BUTTON_FEEDBACK_DURATION: 100,
  /** Error state display duration */
  ERROR_STATE_DURATION: 1000,
  /** Progress update animation interval */
  PROGRESS_UPDATE_INTERVAL: 500,
  /** General countdown interval */
  COUNTDOWN_INTERVAL: 1000,
  /**
   * Delay between sequential letter reveals in the animation queue (ms).
   * Deliberately EXCEEDS LETTER_REVEAL_DURATION so each tile's flip fully lands
   * and rests for a beat before the next one drops, rather than the next tile
   * starting mid-flip. Matches the imported design cadence (FL_STAGGER_MS);
   * see #430 (supersedes the "kept close" tuning from #405).
   *
   * Mirrored in `lexicontest_core::constants`, which derives the server's
   * ready-gate deadline from it. Changing this alone shipped #443; update the
   * mirror in the same commit or `reveal_timing_contract_tests` fails.
   */
  LETTER_QUEUE_STAGGER_MS: 620,
  /**
   * How long the letter grid takes to travel from the selection screen's
   * position to the word-building screen's during the phase handover (#438).
   *
   * Owned here because JS applies the travel transition inline on the ghost
   * element while CSS drives the two layers' fades; the `.phase-handover-*`
   * rules in styles/game-animations.css are written against this value and the
   * two must be changed together.
   */
  PHASE_HANDOVER_TRAVEL_MS: 420,
  /**
   * Ceiling on the handover before it is force-finished.
   *
   * The handover normally ends on two events of its own: the word-building
   * layer's enter transition finishing, and the ghost landing. A backgrounded
   * tab clamps the timers that drive both, and a stranded ghost would sit over
   * the live round, so this bounds the wait. Must exceed whichever runs longer —
   * the enter transition in game-animations.css (170ms delay plus 300ms), or
   * PHASE_HANDOVER_TRAVEL_MS plus the measurement settle the travel waits on.
   */
  PHASE_HANDOVER_WATCHDOG_MS: 700,
} as const;

/**
 * WebSocket connection configuration
 *
 * Supports environment-specific URLs and connection parameters
 */
export const WEBSOCKET_CONFIG = {
  /** Default WebSocket URL for development */
  DEFAULT_URL: 'ws://localhost:3000/ws',
  /** Maximum number of reconnection attempts */
  RECONNECT_ATTEMPTS: 3,
  /** Base delay between reconnection attempts (ms) */
  RECONNECT_DELAY: 1000,
  /** Maximum delay between reconnection attempts (ms) - caps exponential backoff */
  MAX_RECONNECT_DELAY: 30000,
  /** Exponential backoff factor for reconnection delays */
  BACKOFF_FACTOR: 2,
  /**
   * Jitter band applied to the exponential branch of scheduleReconnect(), as
   * multipliers on the computed delay. Read by the scheduler itself, so the
   * bound validateConfiguration() checks and the bound the code applies are the
   * same number rather than a comment claiming they match.
   */
  RECONNECT_JITTER_MIN: 0.7,
  RECONNECT_JITTER_MAX: 1.3,
  /**
   * The reconnect ladder for a server-initiated close, as target start times
   * measured from the close that opened the recovery episode (ms).
   *
   * Offsets, not delays chained onto each other. A failing attempt therefore
   * eats its own rung's slack instead of pushing every later rung out, which is
   * what let attempt 2 land after the give-up window in #510.
   *
   * The first rung is the fast retry that makes a rolling deploy invisible: the
   * attempt lands and nothing is ever shown. The rest exist because that
   * attempt can fail — the pod is draining and answers the upgrade with 503, or
   * the load balancer has no healthy target yet — and cover the window out to
   * SHUTDOWN_RETRY_OFFSETS_MS's last rung.
   */
  SHUTDOWN_RETRY_OFFSETS_MS: [50, 350, 650, 950, 1250, 1550],
  /**
   * Additive jitter on every rung of an episode: [0, spread), drawn once at the
   * close so the whole ladder is fixed the moment the episode opens.
   *
   * It is charged twice. It delays the last rung, so it comes out of the
   * give-up window (1550 + 100 + CONNECT_HANDSHAKE_BUDGET_MS = 1950ms against
   * 2000ms), and it eats into every rung gap, which must stay wider than
   * CONNECT_FAILURE_BUDGET_MS. Both bounds are checked by
   * assertReconnectLadderFitsGiveUpWindow().
   */
  SHUTDOWN_RETRY_SPREAD_MS: 100,
  /**
   * How far away a rung's target start has to be for the episode to still
   * schedule it. A rung the previous attempt overran is skipped rather than
   * fired late, so this is the threshold for "already gone", not a floor.
   */
  SHUTDOWN_RETRY_MIN_GAP_MS: 50,
  /**
   * Budgeted time for a connect attempt that fails fast — a 503 or a refused
   * upgrade. Roughly 67ms in run 33668028568: the retry was scheduled at 22.79
   * for 103ms, so the attempt started at 22.893 and its handshake was rejected
   * at 22.96. (The 170ms between scheduling and rejection is that plus the
   * delay, and is not what this budget is.) Set to 150ms, headroom of about
   * double the measurement.
   *
   * The client controls none of this. It is stated so the ladder's rungs can be
   * checked for being spaced wider than a failure takes, instead of pretending
   * failures are free. An attempt that fails slower than this loses its rung's
   * successors, which the episode skips rather than firing late.
   */
  CONNECT_FAILURE_BUDGET_MS: 150,
  /**
   * Budgeted time for a connect attempt that succeeds: TLS plus the HTTP
   * upgrade. Around 140ms in the same run. Set to roughly double that, because
   * the last rung of the shutdown ladder spends it inside the give-up window.
   */
  CONNECT_HANDSHAKE_BUDGET_MS: 300,
  /** Delay before disconnecting inactive connections (ms) */
  DISCONNECT_DELAY: 1000,
  /**
   * Max time to wait for a ReconnectRequest to be answered
   * (ReconnectionSuccess/ReconnectionFailed) before treating the reconnect as a
   * legitimate timeout and recovering to matchmaking (ms). See #299.
   */
  RECONNECT_REQUEST_TIMEOUT: 10000,
  /**
   * How long an active game tolerates a non-connected socket before the app
   * gives up, ends the game and returns the player home with a notice (ms).
   *
   * Derived from what the socket can actually recover inside it, not from a
   * round-trip guess. The case worth protecting is pod switchover on a deploy.
   * A server-initiated close opens a recovery episode whose attempts are
   * scheduled against SHUTDOWN_RETRY_OFFSETS_MS, and the episode does not fall
   * back to exponential backoff when an individual attempt fails — so the last
   * rung starts by 1650ms at worst. Most of the remaining 350ms is not slack,
   * it is the CONNECT_HANDSHAKE_BUDGET_MS that last attempt spends connecting.
   *
   * assertReconnectLadderFitsGiveUpWindow() enforces that, and the equivalent
   * bound for a generic drop, which gets one exponential retry plus a handshake.
   *
   * It is deliberately far below the 30s round timers, which do not pause on
   * disconnect: a wait long enough to burn half a round buys nothing, because
   * an outage that outlasts a couple of seconds will not salvage the round.
   */
  CONNECTION_GIVE_UP_MS: 2000,
} as const;

/**
 * UI configuration constants
 */
export const UI_CONFIG = {
  /** Progress thresholds for different UI states */
  PROGRESS_THRESHOLDS: {
    NEAR_COMPLETE: 95,
    COMPLETE: 100,
    HIGH_PROGRESS: 80,
    LOW_PROGRESS: 30,
  },
  /** Input validation limits */
  VALIDATION: {
    /** Minimum player name length */
    PLAYER_NAME_MIN_LENGTH: 2,
    /** Maximum player name length */
    PLAYER_NAME_MAX_LENGTH: 14,
    /** ASCII alphanumeric only (matches server validation) */
    DISPLAY_NAME_PATTERN: /^[a-zA-Z0-9]+$/,
  },
} as const;

/**
 * Game logic timing configuration
 */
export const GAME_CONFIG = {
  /**
   * Number of letters in a completed selection. Mirrors the server's canonical
   * `lexicontest_core::constants::TOTAL_LETTERS`; the reveal queue drains (and
   * signals ReadyForFinding) only once this many letters are visible, so the two
   * must stay in sync.
   */
  TOTAL_LETTERS: 10,
  /** Extended search timeout (10 seconds) */
  EXTENDED_SEARCH_TIMEOUT: 10000,
  /** Maximum search timeout (1 minute) */
  MAX_SEARCH_TIMEOUT: 60000,
  /**
   * Bot-game startup timeout (8 seconds). A bot match is created server-side
   * almost instantly, so this is deliberately far shorter than the human
   * matchmaking window — it is only a safety net so a bot match that never
   * confirms surfaces an error instead of hanging on "Setting up your game".
   */
  BOT_START_TIMEOUT: 8000,
  /** Game duration display text */
  GAME_DURATION_TEXT: '30 Seconds',
  /** Minimum time to display the match-found screen (ms) */
  MATCH_FOUND_DISPLAY_MS: 2000,
} as const;

/**
 * Development server configuration
 *
 * Security-focused configuration for Vite development server
 */
export const DEV_SERVER_CONFIG = {
  /** Localhost-only to prevent accidental network exposure */
  DEFAULT_HOST: 'localhost',
  DEFAULT_PORT: 5174,
  /** Avoid privileged ports below 1024 */
  MIN_PORT: 1024,
  MAX_PORT: 65535,
  /** Valid host values for security validation */
  VALID_HOSTS: ['localhost', '127.0.0.1', '0.0.0.0'] as readonly string[],
} as const;

/**
 * Authentication configuration
 */
export const AUTH_CONFIG = {
  /** Delay before showing loading spinner during auth operations (ms) */
  LOADING_SPINNER_DELAY: 500,
  /** Simulated delay for mock auth API calls (ms) */
  MOCK_API_DELAY: 400,
  /** Minimum password length */
  PASSWORD_MIN_LENGTH: 8,
  /** Check whether mock auth is enabled via env var */
  isMockAuthEnabled: (): boolean => {
    return import.meta.env.VITE_MOCK_AUTH === 'true';
  },
} as const;

/**
 * Environment-specific configuration
 */
export const ENV_CONFIG = {
  /** Current environment */
  environment: import.meta.env.MODE || 'development',

  /** Get WebSocket URL based on environment */
  getWebSocketUrl: (): string => {
    // Runtime override for integration tests (set via page.addInitScript)
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).__LEXICONTEST_WS_URL__
    ) {
      return (window as unknown as Record<string, unknown>).__LEXICONTEST_WS_URL__ as string;
    }

    const env = import.meta.env.MODE;

    if (env === 'production') {
      return import.meta.env.VITE_WEBSOCKET_URL || 'wss://lexicontest.com/ws';
    }

    return WEBSOCKET_CONFIG.DEFAULT_URL;
  },

  /** Get API base URL based on environment */
  getApiBaseUrl: (): string => {
    // Runtime override for integration tests (set via page.addInitScript)
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).__LEXICONTEST_API_URL__
    ) {
      return (window as unknown as Record<string, unknown>).__LEXICONTEST_API_URL__ as string;
    }

    const env = import.meta.env.MODE;

    if (env === 'production') {
      return import.meta.env.VITE_API_URL || 'https://lexicontest.com';
    }

    return 'http://localhost:3000';
  },

  /**
   * Get the public site base URL where player-facing pages (e.g. /signup) are
   * served. This is NOT derived from window.location.origin because the mgmt
   * console is served from a separate origin (mgmt.ops.lexicontest.com); using
   * the current origin there would generate dead invite links pointing at the
   * mgmt host instead of the public site.
   */
  getSiteBaseUrl: (): string => {
    // Runtime override for integration tests (set via page.addInitScript)
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).__LEXICONTEST_SITE_URL__
    ) {
      return (window as unknown as Record<string, unknown>).__LEXICONTEST_SITE_URL__ as string;
    }

    const env = import.meta.env.MODE;

    if (env === 'production') {
      return import.meta.env.VITE_SITE_URL || 'https://lexicontest.com';
    }

    return `http://localhost:${DEV_SERVER_CONFIG.DEFAULT_PORT}`;
  },

  /**
   * Get mgmt API base URL. The mgmt SPA is served same-origin with /mgmt/api/*
   * from the server pod (issue #249), so relative URLs work everywhere by
   * default. The window override exists so per-test fixtures can point at an
   * isolated backend.
   */
  getMgmtApiBaseUrl: (): string => {
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).__LEXICONTEST_MGMT_API_URL__
    ) {
      return (window as unknown as Record<string, unknown>).__LEXICONTEST_MGMT_API_URL__ as string;
    }
    return '';
  },

  /** Get environment-specific timeout multiplier */
  getTimeoutMultiplier: (): number => {
    return import.meta.env.MODE === 'test' ? 0.1 : 1.0;
  },

  /** Get development server host with security validation */
  getDevServerHost: (): string => {
    const host = import.meta.env.VITE_DEV_HOST || DEV_SERVER_CONFIG.DEFAULT_HOST;
    if (!DEV_SERVER_CONFIG.VALID_HOSTS.includes(host)) {
      throw new Error(
        `Invalid development server host: ${host}. Allowed values: ${DEV_SERVER_CONFIG.VALID_HOSTS.join(', ')}`
      );
    }
    return host;
  },

  /** Get development server port with validation */
  getDevServerPort: (): number => {
    const port = parseInt(
      import.meta.env.VITE_DEV_PORT || DEV_SERVER_CONFIG.DEFAULT_PORT.toString()
    );
    if (isNaN(port) || port < DEV_SERVER_CONFIG.MIN_PORT || port > DEV_SERVER_CONFIG.MAX_PORT) {
      throw new Error(
        `Invalid development server port: ${port}. Must be between ${DEV_SERVER_CONFIG.MIN_PORT} and ${DEV_SERVER_CONFIG.MAX_PORT}`
      );
    }
    return port;
  },

  /** Check if network access is explicitly enabled */
  isNetworkAccessEnabled: (): boolean => {
    return import.meta.env.VITE_ALLOW_NETWORK === 'true';
  },

  /** Get security warning message when network access is enabled */
  getNetworkAccessWarning: (): string => {
    return 'WARNING: Development server is accessible from external networks. This should only be used for team development. Set VITE_ALLOW_NETWORK=false to restrict to localhost.';
  },
} as const;

/** The slice of WEBSOCKET_CONFIG the reconnect-ladder invariant reads. */
export interface ReconnectLadderShape {
  readonly SHUTDOWN_RETRY_OFFSETS_MS: readonly number[];
  readonly SHUTDOWN_RETRY_SPREAD_MS: number;
  readonly SHUTDOWN_RETRY_MIN_GAP_MS: number;
  readonly CONNECT_FAILURE_BUDGET_MS: number;
  readonly CONNECT_HANDSHAKE_BUDGET_MS: number;
  readonly CONNECTION_GIVE_UP_MS: number;
  readonly RECONNECT_DELAY: number;
  readonly RECONNECT_JITTER_MAX: number;
}

/**
 * The reconnect ladder must be able to finish inside the give-up window, or a
 * pod switchover ends the game while the socket is still trying (#510). The
 * bound is on the LAST attempt of a recovery episode, not on one attempt from
 * t=0, which is all the previous guard checked.
 *
 * Two quantities this cannot see, and does not pretend to: how long a failing
 * attempt takes to fail, and how long a succeeding one takes to connect. They
 * enter as CONNECT_FAILURE_BUDGET_MS and CONNECT_HANDSHAKE_BUDGET_MS, which are
 * named measurements from one production trace, not guarantees. What is
 * genuinely enforced is when each attempt is scheduled to *start*; the rest is a
 * stated allowance.
 *
 * Because the shutdown rungs are absolute offsets from the close rather than
 * delays chained onto one another, failure latency does not accumulate down the
 * ladder. A failure that fits inside its rung's gap costs nothing, which is the
 * second check; one that does not costs the rungs it ran past, which the
 * scheduler skips rather than firing late.
 *
 * Takes the config as an argument so the failure branches are reachable from a
 * test; WEBSOCKET_CONFIG is `as const`, so a guard reading it directly can never
 * be shown to fire.
 */
export const assertReconnectLadderFitsGiveUpWindow = (ws: ReconnectLadderShape): void => {
  const offsets = ws.SHUTDOWN_RETRY_OFFSETS_MS;
  if (offsets.length < 1) {
    throw new Error('SHUTDOWN_RETRY_OFFSETS_MS must have at least one rung');
  }

  offsets.forEach((offset, i) => {
    if (offset < 0) {
      throw new Error(`SHUTDOWN_RETRY_OFFSETS_MS[${i}] must be >= 0`);
    }
    if (i > 0 && offset <= offsets[i - 1]) {
      throw new Error(
        `SHUTDOWN_RETRY_OFFSETS_MS must increase: [${i}]=${offset} <= [${i - 1}]=${offsets[i - 1]}`
      );
    }
  });

  if (ws.SHUTDOWN_RETRY_MIN_GAP_MS <= 0) {
    throw new Error('SHUTDOWN_RETRY_MIN_GAP_MS must be > 0');
  }

  // Rungs spaced wider than a fast failure takes. Any closer and an attempt
  // that fails within its budget still costs the next rung, so the ladder
  // silently runs shorter than it reads.
  let tightestGap = Infinity;
  for (let i = 1; i < offsets.length; i++) {
    const gap = offsets[i] - (offsets[i - 1] + ws.SHUTDOWN_RETRY_SPREAD_MS);
    tightestGap = Math.min(tightestGap, gap);
    if (gap < ws.CONNECT_FAILURE_BUDGET_MS) {
      throw new Error(
        `Shutdown ladder rung ${i} starts ${gap}ms after rung ${i - 1}'s worst-case start, ` +
          `less than CONNECT_FAILURE_BUDGET_MS (${ws.CONNECT_FAILURE_BUDGET_MS}ms)`
      );
    }
  }

  // The skip threshold cannot exceed a gap, or a rung is skipped even when the
  // previous attempt failed instantly — the ladder would lose rungs no failure
  // latency accounts for.
  if (ws.SHUTDOWN_RETRY_MIN_GAP_MS > tightestGap) {
    throw new Error(
      `SHUTDOWN_RETRY_MIN_GAP_MS (${ws.SHUTDOWN_RETRY_MIN_GAP_MS}ms) is wider than the ` +
        `tightest rung gap (${tightestGap}ms), so a rung is skipped even after an instant failure`
    );
  }

  const lastStart = offsets[offsets.length - 1] + ws.SHUTDOWN_RETRY_SPREAD_MS;
  const worstRecovery = lastStart + ws.CONNECT_HANDSHAKE_BUDGET_MS;
  // Strict: landing exactly on the window puts the last handshake and the
  // give-up on the same instant, with nothing deciding which wins.
  if (worstRecovery >= ws.CONNECTION_GIVE_UP_MS) {
    throw new Error(
      `The shutdown reconnect ladder cannot finish inside the give-up window: the last ` +
        `attempt starts by ${lastStart}ms and is allowed ${ws.CONNECT_HANDSHAKE_BUDGET_MS}ms ` +
        `to connect (${worstRecovery}ms), which is not strictly inside ` +
        `CONNECTION_GIVE_UP_MS (${ws.CONNECTION_GIVE_UP_MS}ms). Drop a rung, pull the offsets ` +
        `in, or raise the window.`
    );
  }

  // The same honesty for a generic drop, which takes the exponential branch and
  // gets exactly one scheduled attempt inside the window.
  const genericRecovery =
    ws.RECONNECT_DELAY * ws.RECONNECT_JITTER_MAX + ws.CONNECT_HANDSHAKE_BUDGET_MS;
  if (genericRecovery >= ws.CONNECTION_GIVE_UP_MS) {
    throw new Error(
      `CONNECTION_GIVE_UP_MS (${ws.CONNECTION_GIVE_UP_MS}ms) is below the first exponential ` +
        `retry plus a handshake (${genericRecovery}ms)`
    );
  }
};

/**
 * Validates configuration at application startup
 * Throws descriptive errors for missing/invalid configuration
 */
export const validateConfiguration = (): void => {
  // Validate WebSocket URL format
  const wsUrl = ENV_CONFIG.getWebSocketUrl();
  if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
    throw new Error(`Invalid WebSocket URL: ${wsUrl}`);
  }

  // Validate numeric ranges
  if (WEBSOCKET_CONFIG.RECONNECT_ATTEMPTS < 1) {
    throw new Error('RECONNECT_ATTEMPTS must be >= 1');
  }

  if (WEBSOCKET_CONFIG.RECONNECT_DELAY < 100) {
    throw new Error('RECONNECT_DELAY must be >= 100ms');
  }

  if (WEBSOCKET_CONFIG.MAX_RECONNECT_DELAY < WEBSOCKET_CONFIG.RECONNECT_DELAY) {
    throw new Error('MAX_RECONNECT_DELAY must be >= RECONNECT_DELAY');
  }

  if (WEBSOCKET_CONFIG.BACKOFF_FACTOR < 1) {
    throw new Error('BACKOFF_FACTOR must be >= 1');
  }

  if (WEBSOCKET_CONFIG.DISCONNECT_DELAY < 100) {
    throw new Error('DISCONNECT_DELAY must be >= 100ms');
  }

  if (WEBSOCKET_CONFIG.RECONNECT_REQUEST_TIMEOUT < 1000) {
    throw new Error('RECONNECT_REQUEST_TIMEOUT must be >= 1000ms');
  }

  assertReconnectLadderFitsGiveUpWindow(WEBSOCKET_CONFIG);

  // Validate animation timing
  if (ANIMATION_CONFIG.LETTER_REVEAL_DURATION < 100) {
    throw new Error('LETTER_REVEAL_DURATION must be >= 100ms');
  }

  if (ANIMATION_CONFIG.BUTTON_FEEDBACK_DURATION < 50) {
    throw new Error('BUTTON_FEEDBACK_DURATION must be >= 50ms');
  }

  if (ANIMATION_CONFIG.LETTER_QUEUE_STAGGER_MS < 50) {
    throw new Error('LETTER_QUEUE_STAGGER_MS must be >= 50ms');
  }

  if (ANIMATION_CONFIG.PHASE_HANDOVER_WATCHDOG_MS <= ANIMATION_CONFIG.PHASE_HANDOVER_TRAVEL_MS) {
    throw new Error('PHASE_HANDOVER_WATCHDOG_MS must exceed PHASE_HANDOVER_TRAVEL_MS');
  }

  // Validate UI constraints
  if (UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH < 1) {
    throw new Error('PLAYER_NAME_MIN_LENGTH must be >= 1');
  }

  if (UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH < UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH) {
    throw new Error('PLAYER_NAME_MAX_LENGTH must be >= PLAYER_NAME_MIN_LENGTH');
  }

  // Validate game timing
  if (GAME_CONFIG.EXTENDED_SEARCH_TIMEOUT < 1000) {
    throw new Error('EXTENDED_SEARCH_TIMEOUT must be >= 1000ms');
  }

  if (GAME_CONFIG.MAX_SEARCH_TIMEOUT <= GAME_CONFIG.EXTENDED_SEARCH_TIMEOUT) {
    throw new Error('MAX_SEARCH_TIMEOUT must be > EXTENDED_SEARCH_TIMEOUT');
  }

  if (GAME_CONFIG.BOT_START_TIMEOUT >= GAME_CONFIG.MAX_SEARCH_TIMEOUT) {
    throw new Error('BOT_START_TIMEOUT must be < MAX_SEARCH_TIMEOUT');
  }

  // Validate development server configuration
  try {
    ENV_CONFIG.getDevServerHost();
  } catch (error) {
    throw new Error(
      `Development server host validation failed: ${error instanceof Error ? error.message : String(error)}`
    );
  }

  try {
    ENV_CONFIG.getDevServerPort();
  } catch (error) {
    throw new Error(
      `Development server port validation failed: ${error instanceof Error ? error.message : String(error)}`
    );
  }

  // Log security warning if network access is enabled
  if (ENV_CONFIG.isNetworkAccessEnabled()) {
    console.warn(ENV_CONFIG.getNetworkAccessWarning());
  }
};

/**
 * Version check configuration for stale client detection
 */
export const VERSION_CHECK_CONFIG = {
  /** How often to poll /version.json (ms) */
  POLL_INTERVAL: 5 * 60 * 1000,
  /** Path to version.json (relative to origin) */
  VERSION_ENDPOINT: '/version.json',
} as const;

export type AuthConfigType = typeof AUTH_CONFIG;
export type AnimationConfigType = typeof ANIMATION_CONFIG;
export type WebSocketConfigType = typeof WEBSOCKET_CONFIG;
export type DevServerConfigType = typeof DEV_SERVER_CONFIG;
export type UIConfigType = typeof UI_CONFIG;
export type GameConfigType = typeof GAME_CONFIG;
export type VersionCheckConfigType = typeof VERSION_CHECK_CONFIG;
